import { type User, UserSchema } from '@liftai/shared'
import { setUser } from '@sentry/react'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import type * as React from 'react'
import { createContext, useContext, useEffect, useState } from 'react'

export type ImpersonationState = {
  isActive: boolean
  impersonatorUid: string | null
}

export type UserData = {
  user: User | null
  loading: boolean
  impersonation: ImpersonationState
}

const DEFAULT_IMPERSONATION: ImpersonationState = {
  isActive: false,
  impersonatorUid: null,
}

export const UserContext = createContext<UserData>({
  user: null,
  loading: true,
  impersonation: DEFAULT_IMPERSONATION,
})

export const MANUAL_LOGOUT_FLAG = 'manual_logout'
export const FORCE_LOGOUT_FLAG = 'force_logout'
const HAD_USER_BEFORE_FLAG = 'had_user_before'

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [userData, setUserData] = useState<UserData>({
    user: null,
    loading: true,
    impersonation: DEFAULT_IMPERSONATION,
  })

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), async (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email ?? undefined,
        })

        try {
          // Get ID token with custom claims
          const idTokenResult = await firebaseUser.getIdTokenResult()
          const claims = idTokenResult.claims as Record<string, unknown>

          // Check if user has custom claims set
          if (!claims.userType) {
            console.warn(
              'User authenticated but custom claims not yet set. This is normal during initial signup/invitation flow.',
            )
            // User is authenticated but claims not set yet - stay in loading state
            // This allows invitation acceptance flow to complete
            setUserData({
              user: null,
              loading: false, // Set to false so UI can proceed
              impersonation: DEFAULT_IMPERSONATION,
            })
            return
          }

          // Base user data from Firebase Auth
          const baseUserData = {
            userId: firebaseUser.uid,
            displayName: firebaseUser.displayName || firebaseUser.email || '',
            email: firebaseUser.email || '',
            phone: firebaseUser.phoneNumber || '',
            createdAt: firebaseUser.metadata.creationTime
              ? new Date(firebaseUser.metadata.creationTime)
              : null,
          }

          // Construct user object based on userType
          let userObject: Record<string, unknown>

          if (claims.userType === 'company') {
            userObject = {
              ...baseUserData,
              userType: 'company',
              role: claims.role,
              companyId: claims.companyId,
              permissions: claims.permissions || {},
              workRouteIds: claims.workRouteIds || [],
            }
          } else if (claims.userType === 'client') {
            userObject = {
              ...baseUserData,
              userType: 'client',
              role: claims.role,
              serviceCompanyId: claims.serviceCompanyId,
              organizationIds: claims.organizationIds || [],
            }
          } else if (claims.userType === 'super_admin') {
            userObject = {
              ...baseUserData,
              userType: 'super_admin',
            }
          } else {
            // No valid userType in claims
            throw new Error('Invalid or missing userType in claims')
          }

          const user = UserSchema.parse(userObject)

          // Detect impersonation from custom token claims
          const isImpersonating = claims.impersonating === true
          const impersonatorUid =
            isImpersonating && typeof claims.impersonatorUid === 'string'
              ? claims.impersonatorUid
              : null

          // Store impersonator UID in sessionStorage as fallback for token refresh
          if (isImpersonating && impersonatorUid) {
            sessionStorage.setItem('impersonator_uid', impersonatorUid)
          }

          if (isImpersonating) {
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email ?? undefined,
              username: `impersonated-by-${impersonatorUid}`,
            })
          }

          // Mark that we had a user (for detecting force logout)
          localStorage.setItem(HAD_USER_BEFORE_FLAG, 'true')

          setUserData({
            user,
            loading: false,
            impersonation: {
              isActive: isImpersonating,
              impersonatorUid:
                impersonatorUid ?? sessionStorage.getItem('impersonator_uid'),
            },
          })
        } catch (error) {
          console.error('Error getting custom claims:', error)
          setUserData({
            user: null,
            loading: false,
            impersonation: DEFAULT_IMPERSONATION,
          })
        }
      } else {
        const hadUserBefore =
          localStorage.getItem(HAD_USER_BEFORE_FLAG) === 'true'
        const isManualLogout =
          localStorage.getItem(MANUAL_LOGOUT_FLAG) === 'true'

        if (hadUserBefore && !isManualLogout) {
          // This is a force logout - user was logged in but Firebase couldn't refresh token
          localStorage.setItem(FORCE_LOGOUT_FLAG, 'true')
          localStorage.removeItem(HAD_USER_BEFORE_FLAG)
        }

        if (isManualLogout) {
          localStorage.removeItem(MANUAL_LOGOUT_FLAG)
          localStorage.removeItem(HAD_USER_BEFORE_FLAG)
        }

        // Clear impersonation sessionStorage on logout
        sessionStorage.removeItem('impersonator_uid')

        setUserData({
          user: null,
          loading: false,
          impersonation: DEFAULT_IMPERSONATION,
        })
      }
    })

    return unsubscribe
  }, [])

  return (
    <UserContext.Provider value={userData}>{children}</UserContext.Provider>
  )
}

export const useCurrentUser = () => useContext(UserContext)

export const useCurrentServiceCompanyUser = () => {
  const { user } = useCurrentUser()
  if (user?.userType !== 'company') {
    throw new Error('User must be a company user')
  }
  return { user }
}

export const useCurrentClientUser = () => {
  const { user } = useCurrentUser()
  if (user?.userType !== 'client') {
    throw new Error('User must be a client user')
  }
  return { user }
}

export const useCurrentSuperAdminUser = () => {
  const { user } = useCurrentUser()
  if (user?.userType !== 'super_admin') {
    throw new Error('User must be a super admin user')
  }
  return { user }
}

export const useCurrentAdminUser = (companyId?: string) => {
  const { user } = useCurrentUser()
  if (user?.userType === 'super_admin') {
    return { user: { ...user, companyId: companyId ?? '' } }
  }
  if (user?.userType !== 'company' || !user?.permissions?.isAdmin) {
    throw new Error('User must be an admin user')
  }
  return { user }
}
